<template>
  <div id="gateways">
    <!-- the user has no fleets => display the warning page -->
    <!-- check true and false to not display anything while it's null -->
    <no-groups v-if="$store.getters.userHasFleets===false" groups="fleets" />

    <template v-if="$store.getters.userHasFleets===true">
      <select-group all group="fleet" />

      <card :title="$t('gateways:filter')" :title-small="titleSmallNbFilters" :visible="false" collapsible>
        <div slot="card-body">
          <div class="form-row">
            <div class="form-group col-sm-4 col-xl-2">
              <label for="filterName" class="control-label">{{ $t('gateways:name') | capitalize }}</label>
              <input id="filterName"
                     v-model="filterName"
                     v-validate="'text'"
                     :data-vv-as="$t('gateways:name')"
                     :class="{ 'is-invalid': errors.has('filterName') }"
                     type="text"
                     name="filterName"
                     class="form-control"
              >
              <div class="invalid-feedback">{{ errors.first('filterName') }}</div>
            </div>

            <div class="form-group col-sm-4 col-xl-2">
              <label for="filterBrand">{{ $t('gateways:brand') | capitalize }}</label>
              <select id="filterBrand" v-model="filterBrand" name="filterBrand" class="form-control">
                <option value="" />
                <option v-for="b in brands" :key="b.id" :value="b.id">{{ b.name | gatewayBrand }}</option>
              </select>
            </div>

            <div class="form-group col-sm-4 col-xl-2">
              <label for="filterDescription" class="control-label">{{ $t('gateways:description') | capitalize }}</label>
              <input id="filterDescription"
                     v-model="filterDescription"
                     v-validate="'text'"
                     :data-vv-as="$t('gateways:description')"
                     :class="{ 'is-invalid': errors.has('filterDescription') }"
                     type="text"
                     name="filterDescription"
                     class="form-control"
              >
              <div class="invalid-feedback">{{ errors.first('filterDescription') }}</div>
            </div>

            <div class="form-group col-sm-12 col-xl-6">
              <label class="control-label">{{ $t('gateways:eui') | capitalize }}</label>
              <multiselect id="eui"
                           v-model="filterEui"
                           :options="euiList"
                           :multiple="true"
                           :internal-search="false"
                           :clear-on-select="false"
                           :close-on-select="true"
                           :show-labels="false"
                           :options-limit="maxPageSizePerFilterRequest + 1"
                           :placeholder="$t('gateways:typeAndSelectGateways') | capitalize"
                           track-by="eui"
                           label="eui"
                           @search-change="getEuiList"
              >
                <span slot="noResult">{{ $t('gateways:noData') | capitalize }}</span>
              </multiselect>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group col-sm-3">
              <label class="control-label d-block">{{ $t('gateways:status') | capitalize }}</label>

              <div>
                <input id="filter-connection-true" v-model="filtersStates.connection" value="TRUE" type="checkbox">
                <label for="filter-connection-true">{{ $t('gateways:connected') | capitalize }}</label>
              </div>

              <div>
                <input id="filter-connection-false" v-model="filtersStates.connection" value="FALSE" type="checkbox">
                <label for="filter-connection-false">{{ $t('gateways:disconnected') | capitalize }}</label>
              </div>

              <div>
                <input id="filter-connection-null" v-model="filtersStates.connection" value="NULL" type="checkbox">
                <label for="filter-connection-null">{{ $t('gateways:neverConnected') | capitalize }}</label>
              </div>
            </div>

            <div class="form-group col-sm-3">
              <label class="control-label d-block">{{ $t('gateways:backhaul') | capitalize }}</label>

              <div>
                <input id="filter-bearer-gsm" v-model="filtersStates.bearer" value="gsm" type="checkbox">
                <label for="filter-bearer-gsm">{{ $t('gateways:gsm') | capitalize }}</label>
              </div>

              <div>
                <input id="filter-bearer-wlan" v-model="filtersStates.bearer" value="wlan" type="checkbox">
                <label for="filter-bearer-wlan">{{ $t('gateways:wlan') | capitalize }}</label>
              </div>

              <div>
                <input id="filter-bearer-ethernet" v-model="filtersStates.bearer" value="ethernet" type="checkbox">
                <label for="filter-bearer-ethernet">{{ $t('gateways:ethernet') | capitalize }}</label>
              </div>

              <div>
                <input id="filter-bearer-sim1" v-model="filtersStates.bearer" value="sim1" type="checkbox">
                <label for="filter-bearer-sim1">{{ $t('gateways:sim1') | capitalize }}</label>
              </div>

              <div>
                <input id="filter-bearer-sim2" v-model="filtersStates.bearer" value="sim2" type="checkbox">
                <label for="filter-bearer-sim2">{{ $t('gateways:sim2') | capitalize }}</label>
              </div>
            </div>

            <div class="form-group col-sm-3">
              <label class="control-label d-block">{{ $t('gateways:door') | capitalize }}</label>

              <div>
                <input id="filter-door-closed" v-model="filtersStates.door" value="CLOSED" type="checkbox">
                <label for="filter-door-closed">{{ $t('gateways:closed') | capitalize }}</label>
              </div>

              <div>
                <input id="filter-door-open" v-model="filtersStates.door" value="OPEN" type="checkbox">
                <label for="filter-door-open">{{ $t('gateways:open') | capitalize }}</label>
              </div>

              <div>
                <input id="filter-door-na" v-model="filtersStates.door" value="NOT_APPLICABLE" type="checkbox">
                <label for="filter-door-na">{{ $t('gateways:notApplicable') | capitalize }}</label>
              </div>

              <div>
                <input id="filter-door-null" v-model="filtersStates.door" value="NULL" type="checkbox">
                <label for="filter-door-null">{{ $t('gateways:unknown') | capitalize }}</label>
              </div>
            </div>

            <div class="form-group col-sm-3">
              <label class="control-label d-block">{{ $t('gateways:gps') | capitalize }}</label>

              <div>
                <input id="filter-gps-locked" v-model="filtersStates.gps" value="LOCKED" type="checkbox">
                <label for="filter-gps-locked">{{ $t('gateways:locked') | capitalize }}</label>
              </div>

              <div>
                <input id="filter-gps-unlocked" v-model="filtersStates.gps" value="UNLOCKED" type="checkbox">
                <label for="filter-gps-unlocked">{{ $t('gateways:unlocked') | capitalize }}</label>
              </div>

              <div>
                <input id="filter-gps-na" v-model="filtersStates.gps" value="NOT_APPLICABLE" type="checkbox">
                <label for="filter-gps-na">{{ $t('gateways:notApplicable') | capitalize }}</label>
              </div>

              <div>
                <input id="filter-gps-null" v-model="filtersStates.gps" value="NULL" type="checkbox">
                <label for="filter-gps-null">{{ $t('gateways:unknown') | capitalize }}</label>
              </div>
            </div>
          </div>

          <div class="row mt-2">
            <div class="col-sm-3"><h5>{{ $t('gateways:filterOnAlarms') | capitalize }}</h5></div>
            <div class="col-sm-9"><h5>{{ $t('gateways:filterOnValues') | capitalize }}</h5></div>
          </div>

          <div v-for="(stat, indexStat) in stats" :key="indexStat" class="form-row align-items-center">
            <div class="form-group col-sm-3">
              <input :id="'filter-alarm-' + stat.id" v-model="filtersAlarms[stat.key]" value="true" type="checkbox">
              <label :for="'filter-alarm-' + stat.id">{{ $t('gateways:alarm' + stat.i18nLastKey) | capitalize }}</label>
            </div>

            <!-- Values -->
            <div class="form-group col-sm-9 row align-items-center">
              <div class="control-label col-sm-4 col-lg-3">{{ $t('gateways:value' + stat.i18nLastKey) | capitalize }}</div>

              <div class="col-sm-4 col-lg-3 form-inline">
                {{ $t('gateways:from') }}
                <input :id="'filter-value-from-' + stat.id"
                       v-model="filtersValues[stat.key].from"
                       v-validate="'number|min_value:' + minFromValue(stat) + '|max_value:' + maxFromValue(stat)"
                       :name="'filter-value-from-' + stat.id"
                       :data-vv-as="$t('from')"
                       :class="{ 'is-invalid': errors.has('filter-value-from-' + stat.id) }"
                       type="text"
                       class="form-control form-control-sm mx-1"
                       size="3"
                >
                {{ stat.unit }}
                <div class="invalid-feedback">{{ errors.first('filter-value-from-' + stat.id) }}</div>
              </div>

              <div class="col-sm-4 col-lg-3 form-inline">
                {{ $t('gateways:to') }}
                <input :id="'filter-value-to-' + stat.id"
                       v-model="filtersValues[stat.key].to"
                       v-validate="'number|min_value:' + minToValue(stat) + '|max_value:' + maxToValue(stat)"
                       :name="'filter-value-to-' + stat.id"
                       :data-vv-as="$t('to')"
                       :class="{ 'is-invalid': errors.has('filter-value-to-' + stat.id) }"
                       type="text"
                       class="form-control form-control-sm mx-1"
                       size="3"
                >
                {{ stat.unit }}
                <div class="invalid-feedback">{{ errors.first('filter-value-to-' + stat.id) }}</div>
              </div>
            </div>
          </div>
        </div>

        <div slot="card-footer">
          <button id="filterButton" class="btn btn-primary" @click="filter()">{{ $t('gateways:search') | capitalize }}</button>
        </div>
      </card>

      <grid id="gatewaysGrid"
            :title="$t('gateways:gateways')"
            :columns.sync="columns"
            :paginated-dto="gateways"
            :manual-export="exportAllGateways"
            :custom-export-label="$t('gateways:exportAll')"
            columns-visibility-btn
            @load="filter"
      >
        <template #header-right>
          <progress-circular v-show="exportAllProgress" :progress="exportAllProgress" />
          <!-- Need a link to click on when we get the csv url, can't use download attribute because of IE11 -->
          <a id="exportAllUrl" :href="exportAllUrl" class="hidden" />
        </template>

        <template slot="addAllToSelection">
          <i :title="$t('gateways:addAllToSelection') | capitalize" class="icon-cart-all" @click="addAllToSelection" />
        </template>

        <template slot="selection" slot-scope="props">
          <i v-if="!$store.getters.isGatewayInSelection(props.dto)" :title="$t('gateways:addToSelection') | capitalize" class="icon-cart-add" @click="addToSelection(props.dto)" />
          <i v-else :title="$t('gateways:removeFromSelection') | capitalize" class="icon-cart-remove" @click="removeFromSelection(props.dto)" />
        </template>

        <template slot="eui" slot-scope="props">
          <router-link :to="{name: 'gatewayRoute', params: {eui: props.dto.eui}}">{{ props.dto.eui }}</router-link>
        </template>

        <span slot="name" slot-scope="props" :title="props.dto.name" class="max-width-cell">{{ props.dto.name }}</span>

        <template slot="fleet" slot-scope="props">{{ props.dto.fleet.name }}</template>

        <span slot="description" slot-scope="props" :title="props.dto.description" class="max-width-cell fwc-250">{{ props.dto.description }}</span>

        <template slot="status" slot-scope="props">
          <span v-if="props.dto.connection === true && props.dto.connection_way === 'BSC'" class="text-success">{{ $t('gateways:connected') | capitalize }}</span>
          <span v-else-if="props.dto.connection === true && props.dto.connection_way === 'VPN'" class="text-success">{{ $t('gateways:connectedVpn') | capitalize }}</span>
          <span v-else-if="props.dto.connection === false" class="text-danger">{{ $t('gateways:disconnected') | capitalize }}</span>
          <span v-else class="text-muted">{{ $t('gateways:neverConnected') | capitalize }}</span>
        </template>

        <template slot="door" slot-scope="props">
          <span v-if="props.dto.door === 'CLOSED'" class="text-success">{{ $t('gateways:closed') | capitalize }}</span>
          <span v-else-if="props.dto.door === 'OPEN'" class="text-danger">{{ $t('gateways:open') | capitalize }}</span>
          <span v-else-if="props.dto.door === 'NOT_APPLICABLE'" class="text-muted">{{ $t('gateways:notApplicable') | capitalize }}</span>
          <span v-else class="text-muted">{{ $t('gateways:unknown') | capitalize }}</span>
        </template>

        <template slot="gps" slot-scope="props">
          <span v-if="!$store.getters.gatewayFeature(props.dto, 'gps')" class="text-muted">{{ $t('gateways:notApplicable') | capitalize }}</span>
          <span v-else-if="props.dto.gps === 'LOCKED'" class="text-success">{{ $t('gateways:locked') | capitalize }}</span>
          <span v-else-if="props.dto.gps === 'UNLOCKED'" class="text-danger">{{ $t('gateways:unlocked') | capitalize }}</span>
          <span v-else-if="props.dto.gps === 'NOT_APPLICABLE'" class="text-muted">{{ $t('gateways:notApplicable') | capitalize }}</span>
          <span v-else class="text-muted">{{ $t('gateways:unknown') | capitalize }}</span>
        </template>
      </grid>

      <a v-show="$store.getters.gatewaysSelection.length > 0" id="goToSelectionLink" href="/#/selection/gateways" class="btn btn-primary mt-3">{{ $t('gateways:doActionOnSelection') | capitalize }}</a>
    </template>
  </div>
</template>

<script>
import { isEqual, each, debounce, isEmpty, map, isUndefined, isNull, cloneDeep, remove } from 'lodash-es'
import { mapGetters } from 'vuex'
import to from '@/modules/await-to-js'
import constants from '@/configuration/constants'
import gatewayBrand from '@/filters/gatewayBrand'
import formatDate from 'vue-kerlink/dist/filters/date'
import exportAllGateways from '@/mixins/exportAllGateways'

import NoGroups from '@/components/page/errors/NoGroups'

// Stats
const stats = [
  { id: 'cpu', key: 'CPU', i18nLastKey: 'Cpu', unit: '%', min: 0, max: 100, show: false },
  { id: 'ram', key: 'RAM', i18nLastKey: 'Ram', unit: '%', min: 0, max: 100, show: false },
  { id: 'systemDisk', key: 'SYSTEM_DISK', i18nLastKey: 'SystemDisk', unit: '%', min: 0, max: 100, show: false },
  { id: 'userDisk', key: 'USER_DISK', i18nLastKey: 'UserDisk', unit: '%', min: 0, max: 100, show: false },
  { id: 'rssi', key: 'RSSI', i18nLastKey: 'Rssi', unit: 'dBm', min: -120, max: -50, show: false },
  { id: 'temperature', key: 'TEMPERATURE', i18nLastKey: 'Temperature', unit: '°C', show: false },
  { id: 'voltage', key: 'SUPPLY_POWER_MILLI_VOLT', i18nLastKey: 'Voltage', unit: 'mV', show: false }
]

export default {
  titleKey: 'gateways',

  components: { NoGroups },

  mixins: [exportAllGateways],

  data () {
    return {
      // Grid
      columns: [],
      gateways: {},

      // List in filters
      euiList: [],
      brands: [],

      // Filters
      filterName: '',
      filterBrand: '',
      filterDescription: '',
      filterEui: [],
      filtersStates: { connection: [], bearer: [], door: [], gps: [] },
      filtersAlarms: {},
      // must be the 'key' attributes defined in stats
      filtersValues: { CPU: {}, RAM: {}, SYSTEM_DISK: {}, USER_DISK: {}, RSSI: {}, TEMPERATURE: {}, SUPPLY_POWER_MILLI_VOLT: {} },
      nbFilters: 0
    }
  },

  computed: {
    ...mapGetters(['selectedFleet', 'selectedFleetId', 'gatewaysFilters']),

    stats () { return stats },
    maxPageSizePerFilterRequest () { return constants.maxPageSizePerFilterRequest },

    titleSmallNbFilters () {
      return this.nbFilters > 0 ? this.$t('gateways:appliedFilter', { count: this.nbFilters }) : ''
    }
  },

  watch: {
    selectedFleet (newFleet, oldFleet) {
      // If fleet change, reset gateway filter
      if (!isEqual(newFleet, oldFleet)) {
        this.filterName = ''
        this.filterBrand = ''
        this.filterDescription = ''
        this.filterEui = []
        this.filtersStates = { connection: [], bearer: [], door: [], gps: [] }
        this.filtersAlarms = {}
        this.filtersValues = { CPU: {}, RAM: {}, SYSTEM_DISK: {}, USER_DISK: {}, RSSI: {}, TEMPERATURE: {}, SUPPLY_POWER_MILLI_VOLT: {} }
      }

      // Get page 1
      this.filter({ page: 1 })

      // Get EUI filter list
      this.getEuiList()
    }
  },

  created () {
    this.getGatewayBrands()
    this.createColumns()

    // Set saved filters
    this.filterName = this.gatewaysFilters.name || ''
    this.filterBrand = this.gatewaysFilters.brand || ''
    this.filterDescription = this.gatewaysFilters.description || ''
    this.filterEui = this.gatewaysFilters.eui || []
    this.filtersStates = { connection: [], bearer: [], door: [], gps: [], ...this.gatewaysFilters.states }
    this.filtersAlarms = this.gatewaysFilters.alarms || {}
    this.filtersValues = { CPU: {}, RAM: {}, SYSTEM_DISK: {}, USER_DISK: {}, RSSI: {}, TEMPERATURE: {}, SUPPLY_POWER_MILLI_VOLT: {}, ...this.gatewaysFilters.values }

    // Only if fleet is empty (ie all fleet selected)
    if (isEmpty(this.selectedFleet) && this.selectedFleetId === -1) {
      this.filter()
      this.getEuiList()
    }
  },

  methods: {
    // ============= Init grid columns
    createColumns () {
      this.columns = [
        { headerSlot: 'addAllToSelection', slot: 'selection' },
        { text: this.$t('gateways:eui'), slot: 'eui', sort: 'eui' },
        { text: this.$t('gateways:name'), slot: 'name', sort: 'name' },
        { text: this.$t('gateways:brand'), formatter: g => gatewayBrand(g.brand.name), sort: 'brand.name' },
        { text: this.$t('gateways:description'), slot: 'description', sort: 'description' },
        { text: this.$t('gateways:status'), slot: 'status' },
        { text: this.$t('gateways:backhaul'), formatter: g => g.bearer ? this.$t('gateways:' + g.bearer) : '-' },
        { text: this.$t('gateways:door'), slot: 'door' },
        { text: this.$t('gateways:gps'), slot: 'gps' }
      ]

      const values = [
        { id: 'alarmDate', type: 'alarm', field: 'date', format: formatDate, show: false },
        { id: 'alarmValue', type: 'alarm', field: 'value', show: false },
        { id: 'lastMin', type: 'statistic', field: 'min', show: false },
        { id: 'lastMax', type: 'statistic', field: 'max', show: false },
        { id: 'lastAvg', type: 'statistic', field: 'average', show: false },
        { id: 'lastValueDate', type: 'statistic', field: 'date', format: formatDate, show: false }
      ]

      for (let i = 0; i < stats.length; i++) {
        const group = {
          id: stats[i].id,
          text: this.$t('gateways:' + stats[i].id),
          children: [],
          show: stats[i].show
        }

        for (let j = 0; j < values.length; j++) {
          group.children.push(
            {
              text: this.$t('gateways:' + values[j].id),
              align: values[j].align,
              show: values[j].show,
              formatter: dto => {
                let value
                switch (values[j].type) {
                  case 'alarm':
                    value = this.getLastAlarm(dto, stats[i].key)[values[j].field]
                    break
                  case 'statistic':
                    value = this.getLastStatistic(dto, stats[i].key)[values[j].field]
                    break
                }
                if (values[j].format) {
                  value = values[j].format(value)
                } else if (value && stats[i].unit) {
                  value = value + ' ' + stats[i].unit
                }
                return value
              }
            }
          )
        }

        this.columns.push(group)
      }
    },

    // Get the alarm object from gateway.lastEvents
    getLastAlarm (gateway, alarmType) {
      if (!gateway.lastEvents) return {}

      const lastEvent = gateway.lastEvents.find((lastEvent) => {
        return lastEvent.name === alarmType
      })
      return lastEvent || {}
    },

    // Get the statistics object from gateway.lastStatistics
    getLastStatistic (gateway, statisticsType) {
      if (!gateway.lastStatistics) return {}

      const lastStatistic = gateway.lastStatistics.find((lastStatistic) => {
        return lastStatistic.name === statisticsType
      })
      return lastStatistic || {}
    },

    // ============= Selection actions
    addToSelection (item) {
      this.$store.commit('addGatewayInSelection', item)
    },

    addAllToSelection () {
      each(this.gateways.list, (item) => {
        this.$store.commit('addGatewayInSelection', item)
      })
    },

    removeFromSelection (item) {
      this.$store.commit('removeGatewayFromSelection', item)
    },

    // ============= Filters methods

    async getGatewayBrands () {
      let error, response
      [error, response] = await to(this.$api.getGatewayBrands(this.$store.getters.application), { sort: '+name' })
      if (error) return

      this.brands = response.data
    },

    // Get list of gateways for filters
    getEuiList: debounce(async function (searchQuery = '') {
      let search = []

      // Filter by selected fleet
      if (!isEmpty(this.selectedFleet)) search = this.$utils.addSearch(search, 'eq', this.selectedFleet.id, 'fleet.id')
      if (searchQuery) search = this.$utils.addSearch(search, 'like', '*' + searchQuery.toUpperCase() + '*', 'eui')
      if (search.length > 0) search = JSON.stringify(search.length === 1 ? search[0] : this.$utils.formatSearch('and', search))

      let error, response
      [error, response] = await to(this.$api.getGateways(this.$store.getters.application, { pageSize: constants.maxPageSizePerFilterRequest, sort: '+eui', fields: 'eui', search }))
      if (error || !response) return

      // Add ... if all results are not displayed
      if (response.data.count < response.data.totalCount) response.data.list.push({ eui: '...', $isDisabled: true })
      this.euiList = response.data.list
    }, 500),

    // Calculate min and max for values filters
    minFromValue (stat) {
      return !isNaN(Number(stat.min))
        ? stat.min
        : Number.MIN_SAFE_INTEGER
    },

    // Calculate min and max for values filters
    maxFromValue (stat) {
      return !isNaN(Number(this.filtersValues[stat.key].to))
        ? this.filtersValues[stat.key].to
        : !isNaN(Number(stat.max))
          ? stat.max
          : Number.MAX_SAFE_INTEGER
    },

    // Calculate min and max for values filters
    minToValue (stat) {
      return !isNaN(Number(this.filtersValues[stat.key].from))
        ? this.filtersValues[stat.key].from
        : !isNaN(Number(stat.min))
          ? stat.min
          : Number.MIN_SAFE_INTEGER
    },

    // Calculate min and max for values filters
    maxToValue (stat) {
      return !isNaN(Number(stat.max))
        ? stat.max
        : Number.MAX_SAFE_INTEGER
    },

    async filter (options = {}) {
      const valid = await this.$validator.validateAll()
      if (!valid) return

      // Save filters
      this.$store.commit('setGatewaysFilters', {
        name: this.filterName,
        brand: this.filterBrand,
        description: this.filterDescription,
        eui: this.filterEui,
        states: cloneDeep(this.filtersStates),
        alarms: cloneDeep(this.filtersAlarms),
        values: cloneDeep(this.filtersValues)
      })

      // Add criteria
      let search = []
      if (this.filterName) search = this.$utils.addSearch(search, 'like', '*' + this.filterName + '*', 'name')
      if (this.filterBrand) search = this.$utils.addSearch(search, 'eq', this.filterBrand, 'brand.id')
      if (this.filterDescription) search = this.$utils.addSearch(search, 'like', '*' + this.filterDescription + '*', 'description')
      if (this.filterEui.length > 0) search = this.$utils.addSearch(search, 'in', map(this.filterEui, 'eui'), 'eui')

      each(this.filtersStates, (filterStates, i) => {
        let filters = []

        each(filterStates, filterState => {
          if (filterState !== 'NULL') {
            filters.push(this.$utils.formatSearch('eq', filterState.toUpperCase(), i))
          } else {
            filters.push(this.$utils.formatSearch('eq', null, i))
          }
        })

        if (filters.length === 1) {
          search = this.$utils.addSearch(search, 'eq', filters[0].values, filters[0].operand)
        } else if (filters.length > 1) {
          search = this.$utils.addSearch(search, 'or', filters)
        }
      })

      each(this.filtersAlarms, (filterAlarm, i) => {
        if (filterAlarm) {
          search = this.$utils.addSearch(search, 'and', [
            this.$utils.formatSearch('eq', ['BSC'], 'lastEvents.domain'),
            this.$utils.formatSearch('eq', ['ALARM'], 'lastEvents.type'),
            this.$utils.formatSearch('eq', [false], 'lastEvents.read'),
            this.$utils.formatSearch('eq', [i.toUpperCase()], 'lastEvents.name')
          ])
        }
      })

      each(this.filtersValues, (interval, stat) => {
        const filters = []
        if (!isUndefined(interval.from) && !isNull(interval.from) && interval.from !== '') {
          filters.push(this.$utils.formatSearch('gte', interval.from, 'lastStatistics.average'))
        }
        if (!isUndefined(interval.to) && !isNull(interval.to) && interval.to !== '') {
          filters.push(this.$utils.formatSearch('lte', interval.to, 'lastStatistics.average'))
        }
        if (filters.length > 0) {
          filters.push(this.$utils.formatSearch('eq', [stat.toUpperCase()], 'lastStatistics.name'))
          search = this.$utils.addSearch(search, 'and', filters)
        }
      })

      // Do not count fleet filter in filters count
      this.nbFilters = search.length

      // Filter by selected fleet
      if (!isEmpty(this.selectedFleet)) search = this.$utils.addSearch(search, 'eq', this.selectedFleet.id, 'fleet.id')

      if (search.length > 0) options.search = JSON.stringify(search.length === 1 ? search[0] : this.$utils.formatSearch('and', search))
      this.searchString = options.search

      // Search in selected/all fleet
      let error, response
      [error, response] = await to(this.$api.getGateways(this.$store.getters.application, this.$utils.getParams({ sort: '+eui' }, options, this.queryPageParams)))
      if (error || !response) return

      // Get last events and last stats for each gateways
      this.gateways = response.data

      // We do it after getting data to prevent the column to appear before the data
      const columns = cloneDeep(this.columns)
      // Remove fleet colum
      remove(columns, c => c.slot === 'fleet')
      if (isEmpty(this.selectedFleet)) {
        // All fleets selected, add fleet column
        columns.splice(2, 0, { text: this.$t('gateways:fleet'), slot: 'fleet', sort: 'fleet.name' })
      }
      this.columns = columns
    },

    async getGatewayLastEvents (gateway) {
      let search = []
      search = this.$utils.addSearch(search, 'in', ['CPU', 'RAM', 'SYSTEM_DISK', 'USER_DISK', 'RSSI', 'TEMPERATURE', 'SUPPLY_POWER_MILLI_VOLT'], 'name')
      search = this.$utils.addSearch(search, 'eq', 'ALARM', 'type')

      let error, response
      [error, response] = await to(this.$api.getGatewayLastEvents(gateway, { pageSize: constants.maxPageSizePerFilterRequest, search: JSON.stringify(this.$utils.formatSearch('and', search)), fields: '!links' }))
      if (error || !response) return

      return response.data.list
    },

    async getGatewayLastStatistics (gateway) {
      let error, response
      [error, response] = await to(this.$api.getGatewayLastStatistics(gateway, { fields: '!gateway,!links' }))
      if (error || !response) return

      return response.data.list
    }
  }
}
</script>
